import axios from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { errorMessage, PAGE_SIZE } from './helpers'
import type {
  Category,
  Coverage,
  MappingChanges,
  MappingFilters,
  MappingRow,
  Paged,
  ReviewRow,
} from './ProductCategories.types'

const SEARCH_DEBOUNCE_MS = 300

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

function usePagedList<T>(url: string, params: Record<string, unknown>) {
  const [data, setData] = useState<Paged<T>>({ total: 0, rows: [] })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestId = useRef(0)
  const paramsKey = JSON.stringify(params)

  const load = useCallback(() => {
    const id = ++requestId.current
    setLoading(true)
    axios
      .get<Paged<T>>(url, { params: JSON.parse(paramsKey) as Record<string, unknown> })
      .then(response => {
        if (id !== requestId.current) return
        setData(response.data)
        setError(null)
      })
      .catch(err => {
        if (id === requestId.current) setError(errorMessage(err))
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false)
      })
  }, [url, paramsKey])

  useEffect(load, [load])

  return { ...data, loading, error, setError, load }
}

export function useCoverage() {
  const [coverage, setCoverage] = useState<Coverage | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    axios
      .get<Coverage>('/productCategories/coverage')
      .then(({ data }) => {
        setCoverage(data)
        setError(null)
      })
      .catch(err => setError(errorMessage(err)))
  }, [])

  return { coverage, error }
}

export function useReviewQueue(page: number) {
  const list = usePagedList<ReviewRow>('/productCategories/review', { page, pageSize: PAGE_SIZE })
  const [saving, setSaving] = useState<string | null>(null)

  const approve = (validationCode: string, productCategory: string) => {
    setSaving(validationCode)
    return axios
      .post(`/productCategories/review/${encodeURIComponent(validationCode)}`, { productCategory })
      .then(() => {
        list.setError(null)
        list.load()
      })
      .catch(err => list.setError(errorMessage(err)))
      .finally(() => setSaving(null))
  }

  return { rows: list.rows, total: list.total, loading: list.loading, error: list.error, saving, approve }
}

export function useMappings(filters: MappingFilters, page: number) {
  const search = useDebounced(filters.search, SEARCH_DEBOUNCE_MS)
  const list = usePagedList<MappingRow>('/productCategories/mappings', {
    page,
    pageSize: PAGE_SIZE,
    source: filters.source || undefined,
    search: search || undefined,
    needsReview: filters.needsReview,
  })

  const update = (id: number, changes: MappingChanges) =>
    axios
      .put(`/productCategories/mappings/${id}`, changes)
      .then(() => {
        list.setError(null)
        list.load()
      })
      .catch(err => list.setError(errorMessage(err)))

  return { rows: list.rows, total: list.total, loading: list.loading, error: list.error, update }
}

export function useCategories() {
  const [categories, setCategories] = useState<Category[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    axios
      .get<Category[]>('/productCategories')
      .then(({ data }) => {
        setCategories(data)
        setError(null)
      })
      .catch(err => setError(errorMessage(err)))
  }, [])

  return { categories, error }
}
